import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { Patient } from 'src/app/models/patient.model';
import { VoicemailDropCanaryConfig } from 'src/app/models/voicemail-drop-canary.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { FirestoreService } from 'src/app/services/firestore.service';

/**
 * Decides whether this user may pick a pre-recorded voicemail message before dialling (CI-08 / CI-07).
 *
 * Reads {@link VoicemailDropCanaryConfig} from `welby_configuration/featureToggles` in real time and
 * cross-references the authenticated user's email, so the pilot group can be changed — or the rollout
 * killed — from Firestore without a deploy. Mirrors {@link OutboundCallerIdCanaryService}.
 *
 * Fails closed, and here that means **the staff member speaks the message themselves**: a missing,
 * disabled or malformed config leaves every user with the behaviour that shipped before this feature.
 * Because the call only carries answering machine detection when the dialog sends a selection, failing
 * closed also means no detection runs and no per-call charge is incurred — this toggle is the kill
 * switch for the backend behaviour too, not only for the control.
 *
 * The user side is derived from `isLoggedObservable$` (emits on every login/logout) so a logout resets
 * the evaluation and a shared workstation never inherits the previous user's group.
 */
@Injectable({
  providedIn: 'root',
})
export class VoicemailDropCanaryService {
  private static readonly CONFIG_PATH = 'welby_configuration/featureToggles';
  private static readonly FEATURE_KEY = 'voicemailDropCanary';

  readonly isEnabled$: Observable<boolean> = combineLatest([
    this.firestore.doc$(VoicemailDropCanaryService.CONFIG_PATH).pipe(catchError(() => of(null))),
    this.auth.isLoggedObservable$.pipe(map((isLoggedIn) => (isLoggedIn ? this.auth.user : null))),
  ]).pipe(
    map(([config, user]) => this.evaluate(config?.[VoicemailDropCanaryService.FEATURE_KEY], user)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  // Declared after `isEnabled$` to satisfy member-ordering (public before private). Field initialisers
  // run after the constructor's parameter properties are assigned, so the order is free here.
  private enabledCache = false;

  constructor(private firestore: FirestoreService, private auth: NewAuthService) {
    this.isEnabled$.subscribe((enabled) => (this.enabledCache = enabled));
  }

  /** Synchronous snapshot. Fails closed until the config resolves, i.e. the live message stays. */
  get isEnabled(): boolean {
    return this.enabledCache;
  }

  /**
   * True when this user may choose a pre-recorded message. Requires `enabled` plus an authenticated
   * user; then only `allowAll: true` (explicit opt-in) reaches everyone. An empty/omitted allowlist —
   * or one made up solely of blank entries — resolves to nobody, so flipping `enabled` before the group
   * is populated changes nothing for anyone. Otherwise the email must be listed.
   */
  private evaluate(config: VoicemailDropCanaryConfig | undefined, user: Patient | undefined): boolean {
    try {
      if (!config?.enabled || !user) {
        return false;
      }
      if (config.allowAll) {
        return true;
      }
      // Non-string and blank entries are dropped up front so a hand-edited Firestore doc (a number in
      // `emails`, a stray '') can neither throw nor match, and the length reflects real recipients.
      const emails = (config.emails ?? [])
        .filter((allowed) => typeof allowed === 'string')
        .map((allowed) => allowed.trim().toLowerCase())
        .filter((allowed) => !!allowed);
      if (emails.length === 0 || !user.email) {
        return false;
      }
      return emails.includes(user.email.trim().toLowerCase());
    } catch {
      // Any unexpected/malformed config can do no more than keep the live message, never error the
      // `isEnabled$` stream (which would freeze `enabledCache` at its last value for this singleton).
      return false;
    }
  }
}
